import dayjs from "dayjs";

import type {
	BriefAgentChoice,
	BriefDocumentKind,
	BriefMarket,
	SaveCampaignBriefPayload,
} from "@/hooks/api/use-campaign-briefs";
import { mergeAdjacentSlots, type TimeSlot } from "@/components/schedule/types";

import { MAX_FILE_BYTES } from "./constants";

export interface BriefFormValues {
	title: string;
	dialDate: string | null;
	/** `YYYY-MM-DD`, or "" for a row the client added and has not picked a date for yet. */
	redialDates: string[];
	/** The weekly hours, in the schedule picker's own shape. */
	schedule: TimeSlot[];
	agentChoice: BriefAgentChoice | null;
	agentId: string;
	agentName: string;
	/** What each agent after the first does; "" for one just added. */
	extraAgents: string[];
	/** Each region once, with the language its calls are in, in the order added. */
	markets: BriefMarket[];
	/** Null until the client answers: there is no default either way. */
	poRequired: boolean | null;
	sowRequired: boolean | null;
	notes: string;
	/** Rate card codes of the optional services ticked. */
	addOns: string[];
	files: Partial<Record<BriefDocumentKind, File>>;
}

export const emptyBriefForm = (): BriefFormValues => ({
	title: "",
	dialDate: null,
	redialDates: [],
	schedule: [1, 2, 3, 4, 5].map((day) => ({ day, startTime: "09:00", endTime: "17:00" })),
	agentChoice: null,
	agentId: "",
	agentName: "",
	extraAgents: [],
	markets: [],
	poRequired: null,
	sowRequired: null,
	notes: "",
	addOns: [],
	files: {},
});

export type BriefFormErrors = Partial<Record<string, string>>;

/** The ISO weekday of a `YYYY-MM-DD` date, Monday is 1. */
const isoWeekday = (date: string) => {
	const day = dayjs(date).day();
	return day === 0 ? 7 : day;
};

/**
 * Everything the API would refuse, said before anything is sent. `today` is the client's own
 * calendar date, `YYYY-MM-DD`.
 */
export function validateBriefForm(values: BriefFormValues, today: string): BriefFormErrors {
	const errors: BriefFormErrors = {};

	if (!values.title.trim()) errors.title = "Give the campaign a name";
	if (!values.dialDate) errors.dialDate = "Pick the first dialling date";
	else if (values.dialDate < today) errors.dialDate = "The first dial can't be in the past";
	if (values.schedule.length === 0) errors.schedule = "Choose the dialling hours";

	const hasHours = (date: string) => values.schedule.some(({ day }) => day === isoWeekday(date));
	if (values.dialDate && values.schedule.length > 0 && !hasHours(values.dialDate))
		errors.dialDate = `There are no dialling hours on ${dayjs(values.dialDate).format("dddd")}s`;

	for (const [index, date] of values.redialDates.entries()) {
		if (!date) errors[`redials.${index}`] = "Pick a date, or remove this redial";
		else if (values.dialDate && date <= values.dialDate)
			errors[`redials.${index}`] = "Redials come after the first dial";
		else if (values.redialDates.indexOf(date) !== index) errors[`redials.${index}`] = "This date is already a redial";
		else if (values.schedule.length > 0 && !hasHours(date))
			errors[`redials.${index}`] = `There are no dialling hours on ${dayjs(date).format("dddd")}s`;
	}

	if (!values.agentChoice) errors.agentChoice = "Choose an existing agent or a new one";
	else if (values.agentChoice === "existing" && !values.agentId) errors.agentId = "Pick one of your agents";

	for (const [index, agent] of values.extraAgents.entries()) {
		if (!agent.trim()) errors[`extraAgents.${index}`] = "Say what this agent does, or remove it";
	}

	if (values.markets.length === 0) errors.markets = "Add at least one market";

	if (values.agentChoice === "new" && !values.files.call_flow)
		errors.call_flow = "Upload the call flow for the new agent";
	if (!values.files.lead_list) errors.lead_list = "Upload the lead list";
	else if (!/\.csv$/i.test(values.files.lead_list.name)) errors.lead_list = "The lead list must be a CSV file";

	if (values.poRequired === null) errors.poRequired = "Say whether a PO is required";
	if (values.sowRequired === null) errors.sowRequired = "Say whether an SOW is required";

	for (const [kind, file] of Object.entries(values.files)) {
		if (file && file.size > MAX_FILE_BYTES) errors[kind] = "Files can be at most 25 MB";
	}

	return errors;
}

/** The body the API takes. Only call once {@link validateBriefForm} returns no errors. */
export function toBriefPayload(values: BriefFormValues): SaveCampaignBriefPayload {
	return {
		title: values.title.trim(),
		dialDate: values.dialDate!,
		redialDates: values.redialDates.toSorted(),
		schedule: mergeAdjacentSlots(values.schedule).map(({ day, startTime, endTime }) => ({
			dow: day,
			from: startTime,
			to: endTime,
		})),
		agentChoice: values.agentChoice!,
		agentId: values.agentChoice === "existing" ? values.agentId : null,
		extraAgents: values.extraAgents.map((agent) => agent.trim()),
		markets: values.markets,
		poRequired: values.poRequired!,
		sowRequired: values.sowRequired!,
		notes: values.notes.trim() || null,
		addOns: values.addOns,
	};
}

/** The files to send: a call flow goes only with a new agent, since an existing one has its own. */
export function filesToUpload(values: BriefFormValues): Partial<Record<BriefDocumentKind, File>> {
	const { call_flow: callFlow, ...rest } = values.files;
	return values.agentChoice === "new" && callFlow ? { call_flow: callFlow, ...rest } : rest;
}
